import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deletePopulerCity, getCities, getPopulerCities } from '../../../../../api/endpoints/cmsContent';
import type { City, PopulerCity } from '../../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../../components/ListSearchField';
import { PopulerCityFormDialog } from '../PopulerCityFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — banned under this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + full live curl round-trip
 *  (see api/types/cmsContent.ts header). This page is only ever rendered as a tab inside
 *  `SiteDetailPage` for one specific site, so `siteId` comes in as a fixed prop. Also loads
 *  that same site's `City` list to resolve `city_Id` -> name for display and to feed the form
 *  dialog's city picker. */
export default function PopulerCityListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PopulerCity | null>(null);
  const [deleting, setDeleting] = useState<PopulerCity | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'content', 'populerCities', siteId],
    queryFn: () => getPopulerCities(siteId),
  });
  const citiesQuery = useQuery({ queryKey: ['cms', 'content', 'cities', siteId], queryFn: () => getCities(siteId) });
  const cities: City[] = useMemo(() => citiesQuery.data?.cities ?? [], [citiesQuery.data]);
  const cityNameById = useMemo(() => new Map(cities.map((c) => [c.id, c.name ?? c.displayName ?? `#${c.id}`])), [cities]);

  const rows: PopulerCity[] = useMemo(() => query.data?.populerCities ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.title, row.cityCode, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePopulerCity(id),
    onSuccess: () => {
      enqueueSnackbar('Popüler şehir silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'populerCities'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Popüler Şehirler</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Popüler Şehir
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Başlık, kod, link..." />
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Başlık</TableCell>
              <TableCell>Şehir</TableCell>
              <TableCell>Fiyat</TableCell>
              <TableCell>Öncelik</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı popüler şehir bulunamadı.' : 'Aramayla eşleşen kayıt bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.title ?? '—'}</TableCell>
                <TableCell>{row.city_Id ? cityNameById.get(row.city_Id) ?? `#${row.city_Id}` : '—'}</TableCell>
                <TableCell>
                  {row.price !== undefined ? `${row.price} ${row.priceCurrency ?? ''}`.trim() : '—'}
                </TableCell>
                <TableCell>{row.priority ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PopulerCityFormDialog
        open={createOpen}
        populerCity={null}
        cities={cities}
        siteId={siteId}
        onClose={() => setCreateOpen(false)}
      />
      <PopulerCityFormDialog
        open={!!editing}
        populerCity={editing}
        cities={cities}
        siteId={siteId}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Popüler şehri sil"
        description={`"${deleting?.title ?? ''}" kaydını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
